import React, { useCallback, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import moment from "moment";
import { NavLink } from "react-router-dom";
import { Formik, useFormikContext } from "formik";
import * as Yup from "yup";

import { ModalButtonGroup, ModalSaveButton } from "../../components";
import {
  SelectPaymentFrequency,
  SelectPaymentMethod,
  Date,
  Currency,
} from "../../components/Forms";

import * as select from "../../reducers";
import * as actionsPrograms from "../../actions/programs";
import * as actionsModals from "../../actions/modals";
import * as actionsHelp from "../../actions/help";
import { Programs } from "../../api";

const validationSchema = Yup.object().shape({
  donation_value: Yup.number()
    .required("Required")
    .min(10, "Must be at least $10"),
});

const getPlaceholderText = (type) => {
  switch (type) {
    case "FB-Journl":
      return "Fringe Benefits";
    case "Recurring Journal":
      return "Internal Transfer";
    case "Cheque":
      return "Cheque";
    default:
      return "Select...";
  }
};

const SelectPaymentMethodField = ({
  name,
  creditCard,
  directDebit,
  selectedProgram,
  ...rest
}) => {
  const { values, setValues } = useFormikContext();

  const handleChange = useCallback(
    (nextValue) => {
      const debit_date =
        values.debit_date === "" ? moment().add(1, "day") : values.debit_date;

      const payment_type = nextValue.value;

      switch (nextValue.value) {
        case "Credit Card":
          setValues((prev) => ({
            ...prev,
            payment_id: creditCard.id,
            debit_date,
            payment_type,
          }));
          break;
        case "Direct Debit":
          setValues((prev) => ({
            ...prev,
            payment_id: directDebit.id,
            debit_date,
            payment_type,
          }));
          break;
        case "Direct Deposit":
          // If the payment type is Direct Deposit, the user should not be able to edit the schedule.
          // Note: payment_id here is a payment _type_ ID;
          // we set it to zero in the case of new Direct Deposit payments.
          // This will get picked up in the API method.
          const payment_id = values.payment_id;
          setValues((prev) => ({
            ...prev,
            debit_date: selectedProgram?.debit_date,
            payment_id,
            payment_type,
          }));
          break;
        default:
          // Defult to "Direct Deposit"
          break;
      }
    },
    [setValues, values, selectedProgram, creditCard, directDebit]
  );

  return (
    <SelectPaymentMethod
      onChange={handleChange}
      creditCard={creditCard}
      directDebit={directDebit}
      name={name}
      {...rest}
    />
  );
};

function ProgramEditForm({
  onClose,
  programSaving,
  programError,
  programSuccess,
  creditCard,
  directDebit,
  setProgramEditDone,
  selectedProgram,
}) {
  // Define the state of the save button
  const buttonVariant = useMemo(() => {
    if (programSaving) return "saving";
    if (programError) return "error";
    if (programSuccess) return "success";
    return "";
  }, [programSaving, programError, programSuccess]);

  const has_direct_debit =
    typeof directDebit !== "undefined" &&
    Object.keys(directDebit).length > 0 &&
    directDebit.number !== "";

  const has_credit_card =
    typeof creditCard !== "undefined" &&
    Object.keys(creditCard).length > 0 &&
    creditCard.number !== "";

  const dispatch = useDispatch();

  const onSubmit = useCallback(
    async ({ charity_avatar, ...values }) => {
      const newData = {
        charity_avatar,
        ...values,
        debit_date: moment(values.debit_date).utc().format("YYYY-MM-DD"),
      };

      dispatch(actionsPrograms.setProgramEditSaving());
      try {
        await Programs.update(newData);
        dispatch(actionsPrograms.setPrograms(newData));
        dispatch(actionsPrograms.setProgramEditSuccess());
      } catch (error) {
        dispatch(actionsPrograms.setProgramEditError(error.message));
      } finally {
        dispatch(actionsPrograms.setProgramEditDone());
      }
    },
    [dispatch]
  );

  const initialValues = useMemo(
    () => ({
      ...(selectedProgram || {}),
      debit_date: moment(selectedProgram?.debit_date ?? undefined)
        .utc()
        .toDate(),
    }),
    [selectedProgram]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, values, resetForm, setValues }) => {
        const is_direct_deposit =
          values.payment_type === "Direct Deposit" ? true : false;
        return (
          <form
            id="programsEditForm"
            className="form edit-program__details"
            onSubmit={handleSubmit}
          >
            <div className="form__wrapper">
              <div className="flex-row form__row">
                <div className="flex-row__auto form__amount small-auto">
                  <Currency
                    label="Amount"
                    id="donation_value"
                    name="donation_value"
                    inputClassName="blue"
                  />
                </div>
                <SelectPaymentFrequency
                  label="Frequency"
                  name="donation_schedule"
                  className="flex-row__stretch"
                />
                {values.payment_type !== "Direct Deposit" && (
                  <Date
                    label="Payment Date"
                    name="debit_date"
                    minDate={moment().utc().add(1, "days").toDate()}
                    showDisabledMonthNavigation
                    dateFormat="dd/MM/yyyy"
                  />
                )}
              </div>
              <div className="flex-row flex-row__stretch form__row">
                <SelectPaymentMethodField
                  placeholder={getPlaceholderText(selectedProgram.payment_type)}
                  label="Payment Method"
                  name="payment_type"
                  className={` Select--icon ${
                    getPlaceholderText(selectedProgram.payment_type) !==
                      "Select..." && "internal-payment-method"
                  }`}
                  creditCard={creditCard}
                  directDebit={directDebit}
                />
              </div>
              {(!has_direct_debit || !has_credit_card) && (
                <div className="flex-row form__row">
                  <div className="flex-row__stretch change-password-link">
                    <NavLink
                      to="/settings"
                      className={({ isActive }) =>
                        `triggeer-details-change fs--12 ${
                          isActive ? "active" : ""
                        }`
                      }
                      onClick={(e) => {
                        onClose();
                      }}
                    >
                      Set up credit card or bank account payments
                    </NavLink>
                  </div>
                </div>
              )}

              <div
                className={`flex-row form__row${
                  is_direct_deposit !== true ? " hide mb-0" : ""
                }`}
              >
                <div className="flex-row__stretch">
                  <ul className="direct-deposit-info">
                    <li className="direct-deposit-info__name">
                      <strong>Name</strong>
                      <span>AFES</span>
                    </li>
                    <li className="direct-deposit-info__bsb">
                      <strong>BSB</strong>
                      <span>032-008</span>
                    </li>
                    <li className="direct-deposit-info__number">
                      <strong>Number</strong>
                      <span>130973</span>
                    </li>
                    <li className="direct-deposit-info__reference">
                      <strong>Reference</strong>
                      <span>
                        {values.donation_webcode !== ""
                          ? values.donation_webcode
                          : "FM" + values.donation_id}
                      </span>
                    </li>
                    <li className="direct-deposit-info__international">
                      <strong className="full">
                        For international donations:
                      </strong>
                      <strong>SWIFT Banking Code</strong>
                      <span>WPACAU2SXXX</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex-row form__row">
                <div className="flex-row__stretch form_note">
                  If you would like to stop supporting{" "}
                  {values.donation_recipient}, please email{" "}
                  <a href="mailto:support@afes.org.au">support@afes.org.au</a>{" "}
                  or call 1800 989 028 during office hours.
                </div>
              </div>

              <ModalButtonGroup>
                <div className="flex-row form__finalise-changes">
                  <button
                    className="btn_sec mr--24"
                    onClick={() => {
                      onClose();
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <ModalSaveButton
                    errorMessage={programError}
                    variant={buttonVariant}
                    onClick={handleSubmit}
                    onTryAgain={handleSubmit}
                  >
                    Save Changes
                  </ModalSaveButton>
                </div>
              </ModalButtonGroup>
            </div>
          </form>
        );
      }}
    </Formik>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    programSaving: select.getProgramSaving(state),
    programError: select.getProgramError(state),
    programSuccess: select.getProgramSuccess(state),
    programDone: select.getProgramDone(state),
    creditCard: select.getSettingsCreditCard(state),
    directDebit: select.getSettingsDirectDebit(state),
  };
};

export default connect(mapStateToProps, {
  ...actionsPrograms,
  ...actionsModals,
  ...actionsHelp,
})(ProgramEditForm);
